<template>
  <DetailLayoutFullWidth>
    <template slot="header">
      <BaseHeaderFinancialDashboard 
        :disableSelect="disableSelect"
        @change="handleChangeEvent"
        @refresh="refreshPage"
        @print="printDashboard"
      />
    </template>
    <template slot="main">
      <loading :opacity="darkMode ? 0 : 0.5" :active="isGeneralLoading" :is-full-page="false" :z-index="10" color="var(--primary-DEFAULT)"></loading>
      <div class="print-area mt-2 md:mt-6 space-y-5">
        <div class="flex flex-wrap gap-4"> 
          <single-panel v-if="hasIssuedInvoiceModule" :class="isNegativeValue.issued" class="w-full flex-1 min-w-max" :header="$t('financial_dashboard.invoice_issued')" :content="facturacionEmitidaGeneral" icon="issued">
            <template slot="footer">
              <div v-if="!isNullSubtitle.issued">
                <span :class="isNegativeValue.Subtitulo1" class="font-semibold">{{ subtitleEmitida }}</span>
                <span > {{ $t('financial_dashboard.vs_the_previous_period', {period: dataPeriodSubtitle }) }}</span>
              </div>
              <span v-else>{{ $t('financial_dashboard.no_apply') }}</span>
            </template>
          </single-panel>
          <single-panel v-if="hasReceivedInvoiceModule" :class="isNegativeValue.received" class="w-full flex-1 min-w-max" :header="$t('financial_dashboard.expenses_received')" :content="gastosRecibidosGeneral" icon="recived" icon-class="text-red-400 bg-red-100 dark:text-red-400">
            <template slot="footer">
              <div v-if="!isNullSubtitle.received">
                <span :class="isNegativeValue.Subtitulo2" class="font-semibold">{{ subtitleRecibida }}</span>
                <span > {{ $t('financial_dashboard.vs_the_previous_period', {period: dataPeriodSubtitle }) }}</span>
              </div>
              <span v-else>{{ $t('financial_dashboard.no_apply') }}</span>
            </template>
          </single-panel>
          <single-panel v-if="hasIssuedInvoiceModule" :class="isNegativeValue.pending" class="w-full flex-1 min-w-max" :header="$t('financial_dashboard.outstanding')" :content="pendienteCobroGeneral" icon="pendientecobro">
            <template slot="footer">
              <span :class="isNegativeValue.Subtitulo3" class="font-semibold">{{ subtitleCobro }}</span>
              <span> {{ $t('financial_dashboard.pendingFinancial') }}</span>
            </template>
          </single-panel>
          <single-panel v-if="hasVerifactu" class="w-full flex-1 min-w-max" :header="$t('tipos.reporting_activo.verifactu')" :content="desgloseVerifactuGeneral" icon="verifactu"/>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <BasePanel class="md:col-span-2">
            <template slot="header">
              <div class="flex flex-wrap justify-between gap-4">
                <h3>{{ chartTitle }} ({{ dataPeriod }})</h3>
                <div v-if="false" class="m-auto flex flex-wrap gap-5 justify-center text-secondary-500 dark:text-darkblue-100 text-sm">
                  <span v-for="(data, index) in dataSetsLabel"
                    :key="data.refChart + index"
                    @click="toggleDatasetChart(data.refChart, index)" 
                    class="flex items-center gap-2 cursor-pointer" 
                    :style="{ textDecoration: isDatasetHidden(data.refChart, index) ? 'line-through' : 'none' }">
                    <base-dot-icon :style="{ color: data.color }" />
                    <span>{{ data.label }}</span>
                  </span>
                </div>
              </div>
            </template>
            <template slot="content">
              <Chart id="chart-panel" 
                :ref="refPanelChart"
                type="bar"
                :show-legend="true"
                :expand="true"
                :charData="chartLineDataStatus"
                :parse-format="formatValueDate"
                :x-axis-value-format="formatChartDate"
                y-axis-value-format="compact"
                tooltip-y-value-format="currency"
                y-axis-suffix="€"
              />
            </template>
          </BasePanel>
          <BasePanel>
            <template slot="header">
              <div class="flex flex-wrap justify-between gap-4 mb-4">
                <h3>{{ $t('financial_dashboard.status_breakdown') }}</h3>
                <div>
                  <el-switch
                    v-if="hasIssuedInvoiceModule && hasReceivedInvoiceModule"
                    class="switch-text break-normal both-switch"
                    id="id-switch-invoices-type"
                    :aria-label="invoiceTypeSwitch ? $t('invoices.received') : $t('invoices.issued')"
                    v-model="invoiceTypeSwitch" 
                    :active-text="$t('invoices.received')" 
                    :inactive-text="$t('invoices.issued')"
                    inactive-color="var(--primary-800)"
                    @change="updateDonutData()"
                  />
                  <span v-else>
                    {{hasIssuedInvoiceModule ? $t('invoices.issued') : $t('invoices.received') }}
                  </span>
                </div>
              </div>
            </template>
            <template slot="content">
              <div v-if="!desgloseEstados.length" class="text-secondary-500 dark:text-darkblue-300 italic">
                {{ $t('app.no_data_text') }}
              </div>
              <template v-else>
                <Chart id="chart-doughnut" 
                  :ref="refDoughnutChart"
                  type="doughnut"
                  :show-legend="false"
                  :expand="false"
                  :charData="chartDonutDataStatus"
                  tooltip-value-format="currency"
                  tooltip-suffix="€"
                  
                />
                <div class="flex justify-evenly flex-wrap gap-2 mt-4 text-center text-xs">
                  <div v-for="(data, index) in desgloseEstados" :key="index" class="flex-item">
                    <div 
                      @click="toggleDatasetChart(refDoughnutChart, index)" 
                      class="m-auto text-secondary-500 dark:text-darkblue-100 cursor-pointer" 
                      :style="{ maxWidth: 'fit-content', textDecoration: isDatasetHidden(refDoughnutChart, index) ? 'line-through' : 'none' }">
                      <div class="font-bold min-w-max">
                        <base-dot-icon class="mr-2" :style="{ color: data.color }" />
                        <span>{{ $n(data.value, 'currency') }} €</span>
                      </div>
                      <span>{{ data.label }}</span>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </BasePanel>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <BasePanel v-if="hasIssuedInvoiceModule" class="w-full">
            <template slot="header">
              <h3>{{ $t('app.icons.alerts') }}</h3>
            </template>
            <template slot="content">
              <div v-if="financialDashboardInfo.Alertas.length === 0" class="text-secondary-500 dark:text-darkblue-300 italic">
                {{ $t('financial_dashboard.no_incident_detected') }}
              </div>
              <div v-else class="w-full flex flex-col gap-2">
                <base-alert-box v-for="(alert, index) in financialDashboardInfo.Alertas" :key="index" :title="alert.Titulo" :text-content="alert.Detalle" :type="alert.alertType" class="w-full min-w-0" />
              </div>
            </template>
          </BasePanel>
          <BasePanel v-if="hasIssuedInvoiceModule || hasReceivedInvoiceModule" class="w-full">
            <template slot="header">
              <h3 class="mb-3">{{ $t('financial_dashboard.top_clients') }}</h3>
            </template>
            <template slot="content">
              <div v-if="!clientesFacturacion.length" class="text-secondary-500 dark:text-darkblue-300 italic">
                {{ $t('app.no_data_text') }}
              </div>
              <div v-else class="space-y-3">
                <div v-for="cliente in clientesFacturacion" :key="cliente.id" class="bg-primary-50 dark:bg-darkblue-600 rounded-lg p-2">
                  <div class="flex gap-2 justify-between">
                    <div class="flex flex-col">
                      <span class="text-secondary-600 dark:text-darkblue-100" v-safe-html="cliente.Nombre"></span>
                      <span class="text-xs text-secondary-500 dark:text-darkblue-200"><span :class="{ 'text-primary-800 dark:text-primary-400': cliente.TotalFacturas >= 0, 'text-red-800 dark:text-red-400': cliente.TotalFacturas < 0 }" class="font-semibold">{{ $n(cliente.TotalFacturas, 'integer_group')  }}</span> {{ $t('invoices.header').toLowerCase() }}</span>
                    </div>
                    <span :class="{ 'text-primary-900 dark:text-primary-400': cliente.Total >= 0, 'text-red-900 dark:text-red-400': cliente.Total < 0 }" class="text-lg font-bold">€{{ $n(cliente.Total, 'currency')  }}</span>
                  </div>
                </div>
              </div>
            </template>
          </BasePanel>
          <BasePanel v-if="hasVerifactu" class="w-full order-first xl:order-3 md:col-span-2 xl:col-span-1">
            <template slot="header">
              <h3>{{ $t('uploaderrors.state') }} {{ $t('verifactu.header') }}</h3>
            </template>
            <template slot="content">
              <div v-if="!desgloseValoresVerifactu.length" class="text-secondary-500 dark:text-darkblue-300 italic">
                {{ $t('app.no_data_text') }}
              </div>
              <template v-else>
                <Chart id="chart-doughnut-verifactu" 
                  :ref="refDoughnutChartVerifactu"
                  type="doughnut"
                  :show-legend="false"
                  :expand="false"
                  :charData="chartDonutVerifactuDataStatus"
                  tooltip-value-format="integer_group"
                />
                <div class="space-y-2 mt-4">
                  <div v-for="(data, index) in desgloseValoresVerifactu" :key="index" class="flex items-center justify-between text-xs">
                    <div 
                      @click="toggleDatasetChart(refDoughnutChartVerifactu, index)" 
                      class="flex items-center gap-2 text-secondary-500 dark:text-darkblue-100 cursor-pointer" 
                      :style="{ textDecoration: isDatasetHidden(refDoughnutChartVerifactu, index) ? 'line-through' : 'none' }">
                      <base-dot-icon :style="{ color: data.color }" />
                      <span>{{ data.label }}</span>
                    </div>
                    <span class="font-bold">{{ $n(data.value, 'integer_group') }}</span>
                  </div>
                  <div class="flex items-center justify-between text-xs mt-3 pt-3 border-t border-secondary-300 dark:border-darkblue-50">
                    <span class="text-secondary-500 dark:text-darkblue-100">{{ $t('financial_dashboard.digital_fingerprint_chain') }}</span>
                    <span class="rounded-full px-2.5 py-0.5 bg-black/5 dark:bg-white/5 font-bold"> ✓ {{ $t('app.verified_f') }}</span>
                  </div>
                </div>
              </template>
            </template>
          </BasePanel>
        </div>
      </div>
    </template>
  </DetailLayoutFullWidth>
</template>
    
<script>
import { i18n } from '@/i18n'
import { COLORES_DASHBOARD } from '@/config/colores-dashboard'
import colorsMixin from '@/mixins/colorsMixin'    
import moment from 'moment'

import Loading from 'vue-loading-overlay'
import DetailLayoutFullWidth from '@/components/layout/DetailLayoutFullWidth.vue'
import SinglePanel from '@/components/SinglePanel.vue'
import Chart from '@/components/charts/Chart.vue'

import { getDashboardFinanciero } from '@/services/dashboard/getDashboardFinanciero'

export default {
  name: 'financial-dashboard',
  mixins: [colorsMixin],
  components: {
    DetailLayoutFullWidth,
    SinglePanel,
    Loading,
    Chart
  },
  props: {},
  data () {
    return {
      refPanelChart: 'panelChart',
      refDoughnutChart: 'doughnutChart',
      refDoughnutChartVerifactu: 'doughnutChartVerifactu',
      financialDashboardInfo: {
        DashboardFinanciero: [],
        Alertas: []
      },
      chartLineDataStatus: null,
      chartDonutDataStatus: null,
      chartDonutVerifactuDataStatus: null,
      invoiceTypeSwitch: false,
      dataSets: []
    }
  },
  computed: {
    id () {
      return this.$route.params.id
    },
    clientId () {
      return this.$store.getters['dashboard/getSelectedClient'] || null
    },
    disableSelect () {
      return !this.cifEmpresa || this.isGeneralLoading 
    },
    activePeriod () {
      return this.$store.getters['financialdashboard/activePeriod']
    },
    activePeriods: function () {
      return this.$store.getters['config/ACTIVE_PERIODS']
    },
    dataPeriod () {
      return this.activePeriods.find(p => p.id === this.activePeriod)?.timePeriod ?? this.activePeriod
    },
    isNullSubtitle () {
      return {
        issued: this.dataPerPeriodGeneral?.Subtitulos?.Subtitulo1 === null,
        received: this.dataPerPeriodGeneral?.Subtitulos?.Subtitulo2 === null, 
      }
    },
    isNegativeValue () {
      const isNegativeSubtitle1 = this.dataPerPeriodGeneral?.Subtitulos?.Subtitulo1 < 0
      const isNegativeSubtitle2 = this.dataPerPeriodGeneral?.Subtitulos?.Subtitulo2 < 0
      const isNegativeSubtitle3 = this.dataPerPeriodGeneral?.Subtitulos?.Subtitulo3 < 0

      return {
        issued: { 'content-red-700': this.dataPerPeriodGeneral?.FacturacionEmitida < 0 },
        Subtitulo1: { 'text-red-500': isNegativeSubtitle1, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle1 },
        received: { 'content-red-700': this.dataPerPeriodGeneral?.GastosRecibidos < 0 }, 
        Subtitulo2: { 'text-red-500': isNegativeSubtitle2, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle2 },
        pending: { 'content-red-700': this.dataPerPeriodGeneral?.PendienteCobro < 0 },
        Subtitulo3: { 'text-red-500': isNegativeSubtitle3, 'text-primary-800 dark:text-primary-400': !isNegativeSubtitle3 },
      }
    },
    dataPeriodSubtitle () {
      return this.activePeriods.find(p => p.id === this.activePeriod)?.nameSingular.toLowerCase() ?? this.activePeriod
    },
    cifEmpresa () {
      return this.$store.getters['financialdashboard/cifEmpresa']
    },
    valueDate () {
      return this.$store.getters['financialdashboard/valueDate']
    },
    currentFormat () {
      return this.$store.getters['financialdashboard/currentFormat']
    },
    tiposEstados: function () {
      return this.$store.getters['config/TIPOS_ESTADO']
    },
    tiposEstadosRecibidas: function () {
      return this.$store.getters['config/TIPOS_ESTADO_RECEPCION']
    },
    tiposEstadosVerifactu: function () {
      return this.$store.getters['config/TIPOS_ESTADO_VERIFACTU_DASHBOARD']
    },
    tiposAlertaEstado () {
      return this.$store.getters['config/TIPOS_ALERTA_ESTADO']
    },
    tiposAviso () {
      return this.$store.getters['config/TIPOS_AVISO']
    },
    isGeneralLoading: function () {
      return this.$store.getters.isLoading
    },
    dataPerPeriodGeneral () {
      let picker = this.valueDate 
      if (this.activePeriod ==='semana') {
        picker = moment(picker).format('[Semana] W')
      }
      if (!picker) {
        return this.financialDashboardInfo.DashboardFinanciero.at(-1)
      }
      return this.financialDashboardInfo.DashboardFinanciero.find((p) => p.Fecha === picker)
    },
    facturacionEmitidaGeneral () {
      return `${i18n.n(this.dataPerPeriodGeneral?.FacturacionEmitida ?? 0, 'currency')} €`
    },
    subtitleEmitida () {
      return `${i18n.n(this.dataPerPeriodGeneral?.Subtitulos?.Subtitulo1 ?? 0, 'percent_decimal')}`
    },
    gastosRecibidosGeneral () {
      return `${i18n.n(this.dataPerPeriodGeneral?.GastosRecibidos ?? 0, 'currency')} €`
    },
    subtitleRecibida () {
      return `${i18n.n(this.dataPerPeriodGeneral?.Subtitulos?.Subtitulo2 ?? 0, 'percent_decimal')}`
    },
    pendienteCobroGeneral () {
      return `${i18n.n(this.dataPerPeriodGeneral?.PendienteCobro ?? 0, 'currency')} €`
    },
    subtitleCobro () {
      return i18n.tc('app.n_invoices', this.dataPerPeriodGeneral?.Subtitulos?.Subtitulo3 ?? 0, 'integer_group')
    },
    desgloseVerifactuGeneral () {
      return i18n.n(this.dataPerPeriodGeneral?.DesgloseVerifactu?.Porcentaje ?? 0, 'percent_decimal')
    },
    darkMode: function () {
      return this.$store.getters['app/darkMode']
    },
    isLower1024: function () {
      return this.$store.getters['app/getIsLower1024']
    },
    isDatasetHidden () {
      return this.$store.getters['financialdashboard/isDatasetHidden']
    },
    formatChartDate () {
      return this.currentFormat.i18nFormat || this.currentFormat.momentDate
    },
    formatValueDate () {
      return this.currentFormat.momentValueDate
    },
    enabledDates () {
      return this.financialDashboardInfo.DashboardFinanciero.map(df => df.Fecha)
    },
    chartTitle () {
      if (this.hasIssuedInvoiceModule && this.hasReceivedInvoiceModule) {
        return this.$t('financial_dashboard.issued_vs_received')
      }
      if (this.hasIssuedInvoiceModule) {
        return this.$t('financial_dashboard.invoice_issued')
      }
      if (this.hasReceivedInvoiceModule) {
        return this.$t('financial_dashboard.invoice_received')
      }
      return ''
    },
    desgloseEstados () {
      const result = []
      const breakdown = this.dataPerPeriodGeneral?.DesgloseEstados
      if (!Array.isArray(breakdown) || breakdown.length === 0) {
        return result
      }

      const tiposEstado = this.invoiceTypeSwitch ? this.tiposEstadosRecibidas : this.tiposEstados
      for (let i = 0; i < breakdown.length; i++) {
        const estado = breakdown[i]
        const estadoType = tiposEstado.find(x => x.id === parseInt(estado.Estado, 10))
        if (estadoType === undefined || estadoType === null) continue
        result.push({
          label: estadoType?.Nombre ?? estado.Estado,
          value: estado.Total ?? 0,
          color: COLORES_DASHBOARD[result.length] || '#000000',
        })
      }
      return result
    },
    desgloseValoresVerifactu () {
      const breakdown = this.dataPerPeriodGeneral?.DesgloseVerifactu?.DesgloseValores
      if (!Array.isArray(breakdown) || breakdown.length === 0) {
        return []
      }

      return breakdown.map(estado => {
        const verifactuType = this.tiposEstadosVerifactu.find(x => x.id === estado.Estado)
        return {
          label: verifactuType?.Nombre ?? estado.Estado,
          value: estado.Total ?? 0,
          color: verifactuType?.color ?? '#000000'
        }
      })
    },
    clientesFacturacion () {
      return this.dataPerPeriodGeneral?.ClientesFacturacion ?? []
    },
    dataSetsLabel () {
      return this.dataSets
    },
    hasIssuedInvoiceModule () {
      return this.$store.getters['auth/hasIssuedInvoiceModule']
    },
    hasReceivedInvoiceModule () {
      return this.$store.getters['auth/hasReceivedInvoiceModule']
    },
    hasVerifactu () {
      return this.$store.getters['auth/hasVerifactuModule']
    }
  },
  mounted: async function () {
    this.dataSets = []
    if (this.hasIssuedInvoiceModule) {
      this.dataSets.push({ refChart: this.refPanelChart, label: this.$t('invoices.issued_header'), color: this.getColor('--primary-DEFAULT') })
    }
    if (this.hasReceivedInvoiceModule) {
      this.dataSets.push({ refChart: this.refPanelChart, label: this.$t('invoices.received_header'), color: '#FF6B6B' })
    }
  },
  methods: {
    loadChartData () {
      this.chartLineDataStatus = {
        labels: [],
        datasets: []
      }
      if (this.hasIssuedInvoiceModule) {
        this.chartLineDataStatus.datasets.push({
          label: i18n.t('invoices.issued_header'),
          data: [],
          borderColor: this.getColor('--primary-DEFAULT'),
          backgroundColor: 'rgba(87, 168, 249)',
        })
      }
      if (this.hasReceivedInvoiceModule) {
        this.chartLineDataStatus.datasets.push({
          label: i18n.t('invoices.received_header'),
          data: [],
          borderColor: '#FF6B6B',
          backgroundColor: '#FF6B6B',
        })
      }
      this.financialDashboardInfo.DashboardFinanciero.forEach(element => {
        this.chartLineDataStatus.labels.push(element.Fecha)

        let datasetIndex = 0

        if (this.hasIssuedInvoiceModule) {
          this.chartLineDataStatus.datasets[datasetIndex].data.push(element.FacturacionEmitida)
          datasetIndex++
        }

        if (this.hasReceivedInvoiceModule) {
          this.chartLineDataStatus.datasets[datasetIndex].data.push(element.GastosRecibidos)
        }
      })
    },
    loadDoughnutChartData () {
      this.chartDonutDataStatus = {
        labels: [],
        datasets: [
          {
            label: [],
            data: [],
            borderWidth: 0,
            backgroundColor: COLORES_DASHBOARD,
          }
        ]
      }
      this.desgloseEstados.forEach(({ label, value, color }) => {
        this.chartDonutDataStatus.labels.push(label)
        this.chartDonutDataStatus.datasets[0].data.push(value)
        // this.chartDonutDataStatus.datasets[0].backgroundColor.push(color) // Colores personalizados por estado en vez de la paleta fija
      })
    },
    loadDoughnutChartDataVerifactu () {
      this.chartDonutVerifactuDataStatus = {
        labels: [],
        datasets: [
          {
            label: [],
            data: [],
            borderWidth: 0,
            backgroundColor: [],
          }
        ]
      }
      this.desgloseValoresVerifactu.forEach(({ label, value, color }) => {
        this.chartDonutVerifactuDataStatus.labels.push(label)
        this.chartDonutVerifactuDataStatus.datasets[0].backgroundColor.push(color)
        this.chartDonutVerifactuDataStatus.datasets[0].data.push(value)
      })
    },
    async getDashboardGeneral (period = this.activePeriod) {
      try {
        this.$store.commit('setIsLoading', true)
        this.financialDashboardInfo.DashboardFinanciero=[]
        this.financialDashboardInfo.Alertas=[]
        const unitTime = this.$store.getters['financialdashboard/momentTimeUnit']
        const params = {
          idCifEmpresa: this.cifEmpresa?.IdCIFEmpresa ?? null,
          fechaFiltro: period,
          FechaFin: moment(this.valueDate, this.currentFormat.momentValueDate).startOf('day').endOf(unitTime).format('YYYY-MM-DDT[00:00:00]'),
        }
        const dataDashboard= await getDashboardFinanciero(params)
        if (!dataDashboard.blnError) {
          if (dataDashboard.DashboardFinanciero) {
            for (const data of dataDashboard.DashboardFinanciero) {
              this.financialDashboardInfo.DashboardFinanciero.push(this.getDashboardDataContent(data))
            }
          }
          if (dataDashboard.Alertas) {
            for (const data of dataDashboard.Alertas) {
              this.financialDashboardInfo.Alertas.push(this.getAlertContent(data))
            }
          }
          setTimeout(() => {
            this.loadChartData()
            this.loadDoughnutChartData()
            this.loadDoughnutChartDataVerifactu()
          }, 100)
        } else {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', dataDashboard))
        }
      } catch (error) {
        console.error(error)
        this.doFeedback()
      } finally {
        this.$store.commit('setIsLoading', false)
      }
    },
    getDashboardDataContent (item) {
      const data = {
        GastosRecibidos: item.GastosRecibidos,
        PendienteCobro: item.PendienteCobro,
        FacturacionEmitida: item.FacturacionEmitida,
        DesgloseVerifactu: {
          DesgloseValores: [],
          Porcentaje: item.DesgloseVerifactu?.Porcentaje ?? 0
        },
        DesgloseEstados: [],
        Fecha: item.Fecha,
        Subtitulos: {
          Subtitulo1: item.Subtitulos.Subtitulo1,
          Subtitulo2: item.Subtitulos.Subtitulo2,
          Subtitulo3: item.Subtitulos.Subtitulo3,
          Subtitulo4: item.Subtitulos.Subtitulo4
        },
        ClientesFacturacion: []
      }
      if (item.ClientesFacturacion?.length) {
        for (const cliente of item.ClientesFacturacion) {
          data.ClientesFacturacion.push(this.getClienteFacturacionContent(cliente))
        }
      }
      if (item.DesgloseEstados?.length) {
        for (const desglose of item.DesgloseEstados) {
          data.DesgloseEstados.push(this.getDesgloseEstadosContent(desglose))
        }
      }
      if (item.DesgloseVerifactu?.DesgloseValores?.length) {
        for (const desglose of item.DesgloseVerifactu.DesgloseValores) {
          data.DesgloseVerifactu.DesgloseValores.push(this.getDesgloseVeriFactuContent(desglose))
        }
      }
      return data
    },
    getDesgloseEstadosContent (item) {
      return {
        Estado: item.Estado,
        Total: item.Total
      }
    },
    getDesgloseVeriFactuContent (item) {
      return {
        Estado: item.Estado,
        Total: item.Total
      }
    },
    getClienteFacturacionContent (item) {
      return {
        Nombre: this.addBreakPoints(item.Nombre),
        TotalFacturas: item.TotalFacturas,
        Total: item.Total,
        IsReceived: item.IsReceived
      }
    },
    getAlertContent (item) {
      const warningStatus = this.tiposAlertaEstado.find(t => t.id === item.ImportanciaAlerta)
      const idEstado = parseInt(item.Detalle, 10)
      const estadoType = this.tiposEstados.find(e => e.id === idEstado)
      const alertContent={
        Titulo: `${estadoType.Info}`,
        Detalle: `${estadoType.Info}: ${item.Titulo}`,
        Ruta: item.Ruta,
        TipoAlerta: item.TipoAlerta,
        ImportanciaAlerta: item.ImportanciaAlerta,
        alertType: warningStatus?.alertType ?? 'error',
        Fecha: item.Fecha,
        actions: [],
      }
      return alertContent
    },
    handleChangeEvent ({ field, value, refresh }) {
      if (refresh) {
        if (field !== 'valueDate' || !this.enabledDates.includes(value)) {
          this.refreshPage()
        } else {
          setTimeout(() => {
            // this.loadChartData()
            this.loadDoughnutChartData()
            this.loadDoughnutChartDataVerifactu()
          }, 100)
        }
      }
    },
    updateDonutData () {
      setTimeout(() => {
        this.$store.commit('financialdashboard/resetHiddenDatasets')
        this.loadDoughnutChartData()
      }, 100)
    },
    async refreshPage () {
      this.$store.commit('financialdashboard/resetHiddenDatasets')
      await this.getDashboardGeneral()
    },
    toggleDatasetChart (refChart, datasetIndex) {
      this.$store.commit('financialdashboard/toggleHiddenDataset', { refChart, datasetIndex })
      this.$refs[refChart].toggleDatasetVisibility(datasetIndex)
    },
    printDashboard () {
      const printArea = this.$el.querySelector('.print-area')
      if (!printArea) return

      const oldIframe = document.getElementById('printeriframe')
      if (oldIframe) oldIframe.remove()

      const clone = printArea.cloneNode(true)
      const originalCanvas = printArea.querySelectorAll('canvas')

      const chartRefs = {
        'chart-panel': this.refPanelChart,
        'chart-doughnut': this.refDoughnutChart,
        'chart-doughnut-verifactu': this.refDoughnutChartVerifactu,
      }

      clone.querySelectorAll('canvas').forEach((cloneCanvas, index) => {
        if (originalCanvas[index]) {
          const chartId = originalCanvas[index].id
          const isDoughnut = chartId.startsWith('chart-doughnut')
          const chartRef = this.$refs[chartRefs[chartId]]
          const sourceCanvas = chartRef?.trimCanvas() || originalCanvas[index]
          const img = document.createElement('img')
          img.src = sourceCanvas.toDataURL('image/png')
          if (this.isLower1024) {
            img.style.width = isDoughnut ? '40%' : '65%'
          } else {
            img.style.width = isDoughnut ? '40%' : '100%'
          }
          img.style.justifySelf = 'center'
          img.style.alignSelf = 'center'
          cloneCanvas.parentNode.replaceChild(img, cloneCanvas)
        }
      })
      const switchElement = clone.getElementsByClassName('el-switch')[0]
      if (switchElement) {
        const switchStateLabel = this.invoiceTypeSwitch ? this.$t('invoices.received') : this.$t('invoices.issued')
        const uniqueLabel = document.createElement('span')
        uniqueLabel.textContent = switchStateLabel
        switchElement.parentNode.replaceChild(uniqueLabel, switchElement)
      }

      const iframe = document.createElement('iframe')
      iframe.setAttribute('id', 'printeriframe')
      iframe.setAttribute('width', '768px')
      iframe.style.display = 'none'
      document.body.appendChild(iframe)

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document

      const allStyles = [...document.querySelectorAll('link[rel="stylesheet"], style')]
        .map(el => el.outerHTML).join('\n')

      iframeDoc.open()
      iframeDoc.write(`<!DOCTYPE html><html><head><meta charset="utf-8">${allStyles}</head><body>${clone.outerHTML}</body></html>`)
      iframeDoc.close()

      const printStyle = iframeDoc.createElement('style')
      printStyle.textContent = `
        @page { 
          size: A4; 
          margin: 10mm;
        }
        @media print {
          .print-area { 
            shadow: none !important; 
          }
          .kpi-card {
              break-inside: avoid;
              page-break-inside: avoid;
          }
        }
        *, *::before, *::after { 
          box-sizing: border-box; 
        }
        html, body {
          width: 100%;
          height: auto;
          margin: 0;
          padding: 0;
          overflow: visible;
          background: white;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .print-area {
          width: 100%;
          padding: 0;
          margin: 0;
        }
        `
      iframeDoc.head.appendChild(printStyle)

      setTimeout(() => {
        try {
          iframe.contentWindow.focus()
          iframe.contentWindow.print()
        } catch (e) {
          console.error('Error al imprimir:', e)
        }
        setTimeout(() => iframe.remove(), 1000)
      }, 300)
    },
    addBreakPoints (text) {
      const el = document.createElement('div')
      el.textContent = String(text)
      return el.innerHTML.replace(/([_\-.])/g, '$1<wbr>')
    }
  }
}
</script>
<style scoped>
.flex-item {
    flex: 1 1 calc(33.333% - 0.5rem);
}

.active{
  @apply bg-primary-600 text-secondary-50 !important;
}

.active:hover, .active:focus{
 @apply bg-primary-300 !important;
}

@media print {
  body * {
    visibility: hidden;
  }
  .print-area,.print-area * {
    visibility: visible;
  }
  .print-area {
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>
